import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import '../css/OTPVerification.css';
import { apiConfig } from '../config/api';

const RESEND_COOLDOWN_SECONDS = 30;

const OTPVerification = ({ onVerified }) => {
    const [otp, setOtp] = useState('');
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [otpSent, setOtpSent] = useState(false);
    const [sendStatus, setSendStatus] = useState('idle');
    const [cooldown, setCooldown] = useState(0);

    const location = useLocation();
    const navigate = useNavigate();
    const { panNo, phoneNo, fiscalYear } = location.state || {};

    const getOtpErrorMessage = (error) => {
        const serverMessage = error.response?.data?.error;
        const serverCode = error.response?.data?.code;

        if (serverCode === 'OTP_PROVIDER_TOKEN_EXPIRED' || serverCode === 'OTP_PROVIDER_AUTH_FAILED') {
            return 'OTP delivery is temporarily unavailable. Please contact support or try again after the SMS service is restored.';
        }

        if (serverCode === 'OTP_PROVIDER_CONFIG_MISSING') {
            return 'OTP delivery is not configured correctly at the moment. Please contact support.';
        }

        if (serverMessage) {
            return serverMessage;
        }

        return 'Error occurred while sending OTP. Please try again.';
    };

    const hasOtpBeenSent = useRef(false); // Ref to track if OTP has already been sent
    const maskedPhone = phoneNo ? `xxxxxx${phoneNo.slice(-4)}` : 'N/A';

    useEffect(() => {
        if (cooldown <= 0) {
            return undefined;
        }

        const timer = window.setInterval(() => {
            setCooldown((currentCooldown) => {
                if (currentCooldown <= 1) {
                    window.clearInterval(timer);
                    return 0;
                }

                return currentCooldown - 1;
            });
        }, 1000);

        return () => window.clearInterval(timer);
    }, [cooldown]);

    const sendOtp = async (isResend = false) => {
        setSendStatus(isResend ? 'resending' : 'sending');
        setErrorMessage('');

        try {
            const response = await axios.post(`${apiConfig.otpApi}/send-otp`, { mobile: phoneNo });
            if (response.status === 200 && response.data?.success) {
                setOtpSent(true);
                setSendStatus('sent');
                setCooldown(RESEND_COOLDOWN_SECONDS);
            } else {
                setSendStatus('error');
                setErrorMessage('We could not send the OTP. Please try again.');
            }
        } catch (error) {
            setSendStatus('error');
            setErrorMessage(getOtpErrorMessage(error));
        }
    };

    useEffect(() => {
        // Redirect if panNo or phoneNo are missing
        if (!panNo || !phoneNo) {
            navigate('/');
        } else if (!hasOtpBeenSent.current) {
            // Send OTP only once on mount
            sendOtp();
            hasOtpBeenSent.current = true; // Mark as sent
        }
    }, [panNo, phoneNo, navigate]); // No otpSent in dependencies to prevent resending

    const handleOtpSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setErrorMessage('');

        // Check if OTP, PAN, and Phone are provided
        if (!otp || !panNo || !phoneNo || !fiscalYear) {
            setErrorMessage('OTP, PAN Number, Phone Number and Fiscal Year are required.');
            setLoading(false);
            return;
        }

        try {
            const response = await axios.post(`${apiConfig.otpApi}/verify-otp`, {
                otp,
                panNo,
                phoneNo
            });
            // Check response for success
            if (response.data.success) {
                const tdsResponse = await axios.post(`${apiConfig.tdsApi}/fetch-tds`, {
                    panNo,
                    phoneNo,
                    fiscalYear, // Ensure fiscalYear is correctly sent in the request
                    otp
                });

                const tdsData = {
                    ...tdsResponse.data, // Spread all properties from backend response
                    isVerified: true,    // Add verification flag
                    otp,                 // Include OTP for traceability
                    agentName: tdsResponse.data.agent || 'N/A', // Map backend's "agent" to "agentName"
                    taxableAmount: parseFloat(tdsResponse.data.taxable_amount) || 0, // Ensure correct data type
                    tdsAmount: parseFloat(tdsResponse.data.tds_amount) || 0,
                    panNo,
                    fiscalYear: fiscalYear // Use the provided fiscalYear directly
                };
                onVerified(tdsData);
                navigate('/TDSReportPdf', { state: { updatedTdsData: tdsData, isVerified: true } });

            } else {
                setErrorMessage('The OTP you entered is not valid. Please check it and try again.');
            }
        } catch (error) {
            setErrorMessage(error.response?.data?.error || 'We could not verify the OTP right now. Please try again later.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="otp-page">
            <div className="otp-layout">
                <section className="otp-intro">
                    <span className="otp-eyebrow">Secure Access</span>
                    <h1>Verify the OTP to continue to your TDS report</h1>
                    <p className="otp-copy">
                        We will send a 6-digit verification code to your registered mobile number so you can confirm secure access before downloading the report.
                    </p>

                    <div className="otp-meta-grid">
                        <div className="otp-meta-card">
                            <span className="otp-meta-label">PAN Number</span>
                            <strong>{panNo || 'N/A'}</strong>
                        </div>
                        <div className="otp-meta-card">
                            <span className="otp-meta-label">Mobile Number</span>
                            <strong>{maskedPhone}</strong>
                        </div>
                        <div className="otp-meta-card">
                            <span className="otp-meta-label">Fiscal Year</span>
                            <strong>{fiscalYear || 'N/A'}</strong>
                        </div>
                    </div>
                </section>

                <section className="otp-container">
                    <div className="otp-header">
                        <h2>OTP Verification</h2>
                        <p>Enter the one-time password sent to your mobile number.</p>
                    </div>

                    {errorMessage && (
                        <div className="otp-status otp-status-error" aria-live="assertive">
                            <span className="otp-status-title">Something needs attention</span>
                            <p>{errorMessage}</p>
                        </div>
                    )}

                    {(sendStatus === 'sending' || sendStatus === 'resending') && !errorMessage && (
                        <div className="otp-status otp-status-info" aria-live="polite">
                            <span className="otp-status-title">Sending OTP</span>
                            <p>We are sending a verification code to {maskedPhone}. Please wait a moment.</p>
                        </div>
                    )}

                    {otpSent && sendStatus === 'sent' && !errorMessage && (
                        <div className="otp-status otp-status-success" aria-live="polite">
                            <span className="otp-status-title">OTP sent</span>
                            <p>A 6-digit OTP was sent to {maskedPhone}.</p>
                        </div>
                    )}

                    <form onSubmit={handleOtpSubmit} className="otp-form">
                        <div className="otp-field">
                            <label htmlFor="otp-input">Enter OTP</label>
                            <input
                                type="text"
                                id="otp-input"
                                value={otp}
                                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                placeholder="Enter 6-digit OTP"
                                required
                                disabled={loading}
                                inputMode="numeric"
                                maxLength={6}
                                aria-label="Enter the OTP sent to your phone number"
                            />
                        </div>

                        <button type="submit" disabled={loading} aria-busy={loading}>
                            {loading ? 'Verifying OTP...' : 'Verify and Continue'}
                        </button>
                    </form>

                    <div className="otp-actions">
                        <button
                            type="button"
                            className="otp-secondary-button"
                            onClick={() => sendOtp(true)}
                            disabled={loading || sendStatus === 'sending' || sendStatus === 'resending' || cooldown > 0}
                        >
                            {sendStatus === 'resending'
                                ? 'Resending OTP...'
                                : cooldown > 0
                                    ? `Resend OTP in ${cooldown}s`
                                    : 'Resend OTP'}
                        </button>
                        <p className="otp-helper-text">
                            Need a fresh code? You can request a new OTP after the cooldown ends.
                        </p>
                    </div>
                </section>
            </div>
        </div>
    );
};

export default OTPVerification;
